import { useRef, useCallback } from "react";
import type { NativeScrollEvent, NativeSyntheticEvent } from "react-native";
import { haptic } from "../haptics";

/**
 * Fires haptics at scroll milestones with hysteresis to prevent jitter.
 *
 * - `"selection"` at 50%
 * - `"success"` at 100%
 * - `"light"` on overscroll (past content end, with 10px dead zone)
 *
 * Returns a scroll handler to pass to `onScroll`.
 */
export function useScrollHaptics() {
  const fired50 = useRef(false);
  const fired100 = useRef(false);
  const firedOverscroll = useRef(false);

  const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    const scrollable = contentSize.height - layoutMeasurement.height;

    if (scrollable <= 0) return;

    const pct = contentOffset.y / scrollable;

    // 100% milestone (reset at 95% for hysteresis)
    if (pct >= 1 && !fired100.current) {
      fired100.current = true;
      haptic("success");
    } else if (pct < 0.95) {
      fired100.current = false;
    }

    // 50% milestone (reset at 45% for hysteresis)
    if (pct >= 0.5 && pct < 1 && !fired50.current) {
      fired50.current = true;
      haptic("selection");
    } else if (pct < 0.45) {
      fired50.current = false;
    }

    // Overscroll haptic (10px dead zone past the end)
    const overscroll = contentOffset.y - scrollable;
    if (overscroll > 10 && !firedOverscroll.current) {
      firedOverscroll.current = true;
      haptic("light");
    } else if (overscroll <= 0) {
      firedOverscroll.current = false;
    }
  }, []);

  return onScroll;
}
